import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BackendService } from './backend.service';
import { AbilityCatalog, AbilityRequest } from '../models/ability-catalog.model';

const EMPTY_CATALOG: AbilityCatalog = {
  abilities: [], comingSoon: [], categoryOrder: [], counts: { total: 0, systems: 0 },
};

/**
 * The read-only Abilities catalog (GET /agenthome/abilities) — every ability on
 * the platform, annotated per-user with hasAccess, plus the coming-soon roadmap.
 * Unlike Studio's grant-filtered, builder-only /tools, this browse surface lists
 * everything to any signed-in user; building/using still happens in Studio.
 *
 * Mirrors the {success,message,body} envelope of agent-catalog.service.ts,
 * but getCatalog() deliberately propagates HTTP
 * errors (no `catchError → empty`) so callers can tell "request failed"
 * apart from "no abilities yet" and offer a retry.
 */
@Injectable({ providedIn: 'root' })
export class AbilitiesService {
  // Open-signal for the read-only Abilities modal (mirrors AgentCatalogService).
  // The modal lives in the layout shell so any surface's rail can open it.
  private readonly _openAbilities$ = new Subject<void>();
  readonly openAbilities$ = this._openAbilities$.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly backend: BackendService,
  ) {}

  triggerOpen(): void {
    this._openAbilities$.next();
  }

  getCatalog(): Observable<AbilityCatalog> {
    const url = this.backend.createAgentsUrl('agenthome/abilities');
    return this.http
      .get<{ success: boolean; message: string; body: AbilityCatalog | null }>(url)
      .pipe(map(res => this.normalize(res?.body)));
  }

  /** Record a request for access to a gated ability, or for a connector that
   *  isn't on the platform yet — optionally with the requester's use_case +
   *  timeframe from the confirmation dialog. Resolves true on success. */
  requestAbility(payload: AbilityRequest): Observable<boolean> {
    const url = this.backend.createAgentsUrl('agenthome/abilities/requests');
    return this.http
      .post<{ success: boolean }>(url, payload)
      .pipe(map(res => !!res?.success), catchError(() => of(false)));
  }

  private normalize(body: AbilityCatalog | null): AbilityCatalog {
    if (!body) return EMPTY_CATALOG;
    // A blank/missing category must never surface as a nameless chip, nav row
    // or group heading on the modal/page — bucket those abilities under "Other"
    // (the API is being fixed to not send blanks, but the UI stays robust).
    const abilities = (body.abilities ?? []).map(a => {
      const category = (a.category ?? '').trim() || 'Other';
      return category === a.category ? a : { ...a, category };
    });
    return {
      abilities,
      comingSoon: body.comingSoon ?? [],
      categoryOrder: body.categoryOrder ?? [],
      counts: body.counts ?? { total: abilities.length, systems: 0 },
    };
  }
}
